// SPDX-License-Identifier: AGPL-3.0-or-later
// SPDX-FileCopyrightText: 2026 Ben Richardson <hi@ben.gy>
//
// THE COST GAP — what comes in, what goes back out, and what the bill is made of.
//
// This view exists because the site's headline ("the land did about three
// quarters of the earning") invites an obvious follow-up question: what
// happened to the farming half? The answer is on this page. Cash costs have
// taken roughly three quarters of every receipts dollar for thirty-five years,
// and the composition of that bill has changed out of all recognition — away
// from things a farm DOES (shearing, hired labour, servicing debt) and toward
// things a farm BUYS (fertiliser, chemicals, contractors, insurance, advice).
//
// WHY THIS VIEW LOADS THE CUBE. core.json already carries the cost lines, but
// it carries no sampling error for them — only the rate-of-return tier, which
// is the tier of a completely different estimate. Painting the rate-of-return
// tier onto a fertiliser bill would be inventing evidence, so this view pays
// for cube.json (~1.2 MB, cached for the session) and tiers every single cost
// cell from its own published RSE. 95.4% of the cost cells have one.

import { loadCore, loadCube } from '../data';
import type { Core, Line, Region } from '../types';
import { CRIMSON, FARMING, GRAPHITE, NEUTRAL, SCOPE_SHORT } from '../theme';
import {
  collapse, evidenceLines, isGap, isOutlineOnly, isRankable, isTokenSized,
  tierOpacity, tierPrefix, TIER_MEANING, TIER_WORD, type Tier,
} from '../uncertainty';
import {
  axisBottom, axisLeft, brokenPath, chartFrame, chartSvg, el, extent, finite,
  hatchDefs, isolatedPoints, linearScale, markTip,
} from '../components/svg';
import { esc, fy, money, num, pct, tipAttr } from '../format';
import { glossIcon, glossTerm } from '../components/glossary';
import { closeButton, openOverlay } from '../overlay';
import { currentRoute, setParams } from '../router';

/* ────────────────────────── the evidence tier, locally ────────────────────── */

// Mirrors `tierOf` in pipeline/parse.mjs exactly. The pipeline tiers only the
// two series it carries into core.json (rate of return, capital appreciation);
// every OTHER variable's tier has to be computed on the client from the cube's
// own per-cell RSE, and it must be computed by the same ladder or this view
// would be applying a second, softer uncertainty policy than the rest of the
// site. RSE bands are ABARES' own guidance (use >25% with caution, treat >50%
// as unreliable); the sample ladder exists because an RSE estimated from five
// farms is itself estimated from five farms.
const RSE_BANDS = { GOOD: 25, CAUTION: 50, POOR: 100 };
const SAMPLE_BANDS = { GOOD: 30, FAIR: 15, POOR: 5 };

function rseTier(rse: number | null): Tier {
  if (rse == null || !Number.isFinite(rse)) return 4;
  const r = Math.abs(rse);
  if (r <= RSE_BANDS.GOOD) return 1;
  if (r <= RSE_BANDS.CAUTION) return 2;
  if (r <= RSE_BANDS.POOR) return 3;
  return 4;
}

function sampleTier(sample: number | null): Tier {
  if (sample == null || !Number.isFinite(sample)) return 4;
  if (sample >= SAMPLE_BANDS.GOOD) return 1;
  if (sample >= SAMPLE_BANDS.FAIR) return 2;
  if (sample >= SAMPLE_BANDS.POOR) return 3;
  return 4;
}

function tierOf(value: number | null, rse: number | null, sample: number | null): Tier {
  if (value == null || !Number.isFinite(value)) return 5;
  return Math.max(rseTier(rse), sampleTier(sample)) as Tier;
}

interface Cell { v: number | null; rse: number | null; sample: number | null; tier: Tier }

const V_COSTS = 'Total cash costs ($)';
const V_RECEIPTS = 'Total cash receipts ($)';

/* ─────────────────────────────── view state ───────────────────────────────── */

type Mode = 'dollars' | 'share';
type Sort = 'size' | 'change';

interface State {
  mode: Mode;
  sort: Sort;
  region: string;
  /** The comparison year for the composition chart. Never the provisional one. */
  year: number;
}

export async function renderCostGap(host: HTMLElement): Promise<void> {
  const [core, cube] = await Promise.all([loadCore(), loadCube()]);

  const varIx = new Map(cube.variables.map((v, i) => [v, i]));
  const regIx = new Map(cube.regions.map((r, i) => [r, i]));
  const yearIx = new Map(cube.years.map((y, i) => [y, i]));

  /** Sample size per region-year — the second half of the tier ladder. */
  const sampleAt = new Map<string, number | null>();
  for (const r of core.regions) for (const s of r.series) sampleAt.set(`${r.name}|${s.year}`, s.sample);

  function cell(variable: string, region: string, year: number): Cell {
    const a = varIx.get(variable); const b = regIx.get(region); const c = yearIx.get(year);
    const sample = sampleAt.get(`${region}|${year}`) ?? null;
    if (a == null || b == null || c == null) return { v: null, rse: null, sample, tier: 5 };
    const v = cube.values[a]?.[b]?.[c] ?? null;
    const rse = cube.rse[a]?.[b]?.[c] ?? null;
    return { v, rse, sample, tier: tierOf(v, rse, sample) };
  }

  const settledLast = core.settledYears[core.settledYears.length - 1];
  const byName = new Map(core.regions.map((r) => [r.name, r]));

  // The default region is the one with the most surveyed farms behind it, not
  // the most interesting one — picking the region that best suits the argument
  // and calling it the default is how a data page cheats.
  const defaultRegion = [...core.regions]
    .sort((a, b) => (b.medianSample ?? 0) - (a.medianSample ?? 0))[0].name;

  const params = currentRoute().params;
  const askedRegion = params.get('region');
  const askedYear = Number(params.get('year'));
  const state: State = {
    mode: params.get('mode') === 'share' ? 'share' : 'dollars',
    sort: params.get('sort') === 'change' ? 'change' : 'size',
    region: askedRegion && byName.has(askedRegion) ? askedRegion : defaultRegion,
    // The provisional year may never be a comparison endpoint: it feeds a
    // ranking of how the bill changed, and the site excludes it from every
    // ranking, mean and trend.
    year: core.settledYears.includes(askedYear) ? askedYear : settledLast,
  };

  host.innerHTML = '';
  host.append(buildHead(core));
  host.append(buildStats(core));

  /* ── section 1: the national gap ─────────────────────────────────────── */
  const gapCard = document.createElement('section');
  gapCard.className = 'card stack';
  host.append(gapCard);

  /* ── section 2: the composition of the bill ──────────────────────────── */
  const compCard = document.createElement('section');
  compCard.className = 'card stack';
  compCard.style.marginTop = 'var(--space-lg)';
  host.append(compCard);

  /* ── section 3: the region leaderboard ───────────────────────────────── */
  const tableCard = document.createElement('section');
  tableCard.className = 'card stack';
  tableCard.style.marginTop = 'var(--space-lg)';
  host.append(tableCard);

  host.append(buildFooterNotes(core));

  /** Chart 1 keeps live handles so a year click repaints nothing — it moves a
   *  marker. Rebuilding the chart would throw away keyboard focus mid-press. */
  const yearMarks = new Map<number, SVGRectElement>();

  function selectYear(y: number): void {
    if (!core.settledYears.includes(y)) return;
    state.year = y;
    for (const [yy, node] of yearMarks) node.classList.toggle('sel', yy === state.year);
    setParams({ year: y });
    renderComposition();
  }

  function selectRegion(name: string): void {
    if (!byName.has(name)) return;
    state.region = name;
    setParams({ region: name });
    for (const row of tableCard.querySelectorAll<HTMLTableRowElement>('tr[data-region]')) {
      markSelected(row, row.dataset.region === name);
    }
    // renderComposition() replaces the control the user is standing on, so the
    // focus has to be carried across the rebuild by name. Without this, choosing
    // a region with the keyboard drops focus to the body and the next Tab starts
    // again from the top of the page.
    const key = focusKey(compCard);
    renderComposition();
    restoreFocus(compCard, key);
  }

  /* ═════════════════════ section 1 — the national gap ═══════════════════ */

  function renderGap(): void {
    gapCard.innerHTML = '';

    const head = document.createElement('div');
    head.innerHTML = `
      <h2 class="card-title">What comes in, and what goes straight back out</h2>
      <p class="card-sub">Per surveyed broadacre farm, every year from ${fy(core.years[0])} to ${fy(core.years[core.years.length - 1])},
        in ${glossTerm('nominal', 'nominal dollars')}. The upper line is everything the farm took in as cash;
        the lower line is everything it paid out as cash costs. The space between them is
        ${glossTerm('farmCashIncome', 'farm cash income')} — the money left in the bank account before
        depreciation and before anyone in the family is paid.</p>`;
    gapCard.append(head);

    const controls = document.createElement('div');
    controls.className = 'controls';
    const seg = document.createElement('div');
    seg.className = 'seg';
    seg.setAttribute('role', 'group');
    seg.setAttribute('aria-label', 'How to measure the gap');
    for (const [key, label] of [['dollars', 'Dollars'], ['share', 'Share of receipts']] as Array<[Mode, string]>) {
      const b = document.createElement('button');
      b.type = 'button';
      b.textContent = label;
      b.dataset.focusKey = `mode:${key}`;
      b.setAttribute('aria-pressed', String(state.mode === key));
      b.addEventListener('click', () => {
        if (state.mode === key) return;
        state.mode = key;
        setParams({ mode: key });
        renderGap();
        restoreFocus(gapCard, `mode:${key}`);
      });
      seg.append(b);
    }
    controls.append(seg);
    gapCard.append(controls);

    const { frame, scroll } = chartFrame();
    const W = 900; const H = 380;
    const m = { t: 20, r: 26, b: 52, l: 78 };
    const svg = chartSvg(W, H, state.mode === 'dollars'
      ? 'Cash receipts and cash costs per surveyed broadacre farm, 1990 to 2025'
      : 'Share of cash receipts kept as farm cash income, 1990 to 2025');
    svg.setAttribute('width', String(W));
    svg.setAttribute('height', String(H));
    svg.style.minWidth = `${W}px`;
    svg.append(hatchDefs('cg-prov', GRAPHITE, 0.5));

    const yrs = core.national.map((n) => n.year);
    const x = linearScale([yrs[0] - 0.5, yrs[yrs.length - 1] + 0.5], [m.l, W - m.r]);
    const bandW = Math.max(4, x(1) - x(0));

    // A single guard covers the one thing this chart must never do: fill the
    // space between the lines when the lower line is on top. Nationally cash
    // costs have never exceeded cash receipts, so the band is safe — but if a
    // future release changes that, the fill is dropped rather than drawn
    // upside-down in a colour that means "what farming earned".
    const everNegative = core.national.some((n) => n.cashIncome != null && n.cashIncome < 0);

    let y: ReturnType<typeof linearScale>;
    if (state.mode === 'dollars') {
      const top = Math.max(1, ...core.national.map((n) => finite(n.receipts))) * 1.06;
      y = linearScale([0, top], [H - m.b, m.t]);
      axisLeft(svg, y, m.l, { format: (v) => money(v), gridTo: W - m.r, title: 'Per surveyed farm' });
    } else {
      y = linearScale([0, 100], [H - m.b, m.t]);
      axisLeft(svg, y, m.l, {
        format: (v) => `${v}%`, ticks: [0, 20, 40, 60, 80, 100],
        gridTo: W - m.r, title: 'Share of cash receipts',
      });
    }
    axisBottom(svg, x, H - m.b, {
      format: (v) => String(v),
      ticks: yrs.filter((v) => v % 5 === 0),
      title: 'Financial year ending',
    });

    const seriesPts = (pick: (n: Core['national'][number]) => number | null) =>
      core.national.map((n) => ({ x: finite(x(n.year)), y: pick(n) == null ? null : finite(y(pick(n) as number)) }));

    const keptShare = (n: Core['national'][number]): number | null =>
      n.receipts != null && n.receipts > 0 && n.cashIncome != null ? (n.cashIncome / n.receipts) * 100 : null;

    if (state.mode === 'dollars') {
      if (!everNegative) {
        // The band, split at every gap: one polygon per unbroken run, never one
        // polygon bridging a missing year.
        for (const run of runs(core.national, (n) => n.receipts != null && n.costs != null)) {
          if (run.length < 2) continue;
          const up = run.map((n) => `${finite(x(n.year)).toFixed(2)},${finite(y(n.receipts as number)).toFixed(2)}`);
          const down = [...run].reverse().map((n) => `${finite(x(n.year)).toFixed(2)},${finite(y(n.costs as number)).toFixed(2)}`);
          svg.append(el('path', {
            d: `M${up.join(' L')} L${down.join(' L')} Z`,
            fill: FARMING, opacity: 0.12, 'pointer-events': 'none',
          }));
        }
      }
      drawSeries(svg, seriesPts((n) => n.costs), GRAPHITE, 2, 'Total cash costs');
      drawSeries(svg, seriesPts((n) => n.receipts), FARMING, 2.4, 'Total cash receipts');
    } else {
      if (!everNegative) {
        for (const run of runs(core.national, (n) => keptShare(n) != null)) {
          if (run.length < 2) continue;
          const line = run.map((n) => `${finite(x(n.year)).toFixed(2)},${finite(y(keptShare(n) as number)).toFixed(2)}`);
          const first = finite(x(run[0].year)); const last = finite(x(run[run.length - 1].year));
          // Below the line: what the farming kept. Above it, up to 100%: the
          // cash costs. Two complementary shares of one dollar, so this is not
          // a stack of two independent series and cannot go negative unless the
          // guard above already dropped it.
          svg.append(el('path', {
            d: `M${first.toFixed(2)},${finite(y(0)).toFixed(2)} L${line.join(' L')} L${last.toFixed(2)},${finite(y(0)).toFixed(2)} Z`,
            fill: FARMING, opacity: 0.18, 'pointer-events': 'none',
          }));
          svg.append(el('path', {
            d: `M${first.toFixed(2)},${finite(y(100)).toFixed(2)} L${line.join(' L')} L${last.toFixed(2)},${finite(y(100)).toFixed(2)} Z`,
            fill: GRAPHITE, opacity: 0.1, 'pointer-events': 'none',
          }));
        }
      }
      drawSeries(svg, seriesPts(keptShare), FARMING, 2.4, 'Share of cash receipts kept as farm cash income');
    }

    // The provisional year is hatched wherever it appears, and no line, mean or
    // ranking on this page is allowed to reach into it.
    if (core.provisionalYear != null) {
      const px = finite(x(core.provisionalYear));
      const prov = el('rect', {
        x: px - bandW / 2, y: m.t, width: bandW, height: H - m.b - m.t,
        fill: 'url(#cg-prov)', opacity: 0.55, 'pointer-events': 'none',
      });
      svg.append(prov);
    }

    // One hoverable column per year, carrying every number for that year.
    yearMarks.clear();
    for (const n of core.national) {
      const cx = finite(x(n.year));
      const provisional = n.year === core.provisionalYear;
      const rect = el('rect', {
        x: cx - bandW / 2, y: m.t, width: bandW, height: H - m.b - m.t,
        fill: 'transparent', class: 'mark',
      });
      const share = keptShare(n);
      markTip(rect, [
        `${fy(n.year)}${provisional ? ' — provisional' : ''}`,
        `Cash receipts ${money(n.receipts, { long: true })}`,
        `Cash costs ${money(n.costs, { long: true })}`,
        `Farm cash income ${money(n.cashIncome, { long: true })}${share == null ? '' : ` (${pct(share)} of receipts)`}`,
        // No tier: ABARES publishes no standard error and no sample count for
        // the national series in this extract, and inventing tier 4 for it
        // would mark the published national aggregate "no basis".
        'National published estimate — no sampling error is published for it',
        provisional
          ? 'Provisional. Excluded from every ranking and comparison on this page.'
          : 'Click to compare the cost bill in this year',
      ]);
      if (!provisional) {
        rect.style.cursor = 'pointer';
        rect.setAttribute('tabindex', '0');
        rect.setAttribute('role', 'button');
        rect.addEventListener('click', () => selectYear(n.year));
        rect.addEventListener('keydown', (e) => {
          const ev = e as KeyboardEvent;
          if (ev.key === 'Enter' || ev.key === ' ') { ev.preventDefault(); selectYear(n.year); }
        });
        if (n.year === state.year) rect.classList.add('sel');
        yearMarks.set(n.year, rect);
      }
      svg.append(rect);
    }

    scroll.append(svg);
    gapCard.append(frame);

    const legend = document.createElement('div');
    legend.className = 'legend';
    legend.innerHTML = state.mode === 'dollars'
      ? `${swatch(FARMING, 'Total cash receipts — what the farming earned')}
         ${swatch(GRAPHITE, 'Total cash costs — money leaving the farm')}
         ${swatch(FARMING, 'The gap: farm cash income', 0.12)}
         ${swatch(GRAPHITE, 'Provisional year, hatched and excluded', 0.4)}`
      : `${swatch(FARMING, 'Kept as farm cash income', 0.35)}
         ${swatch(GRAPHITE, 'Paid out as cash costs', 0.2)}
         ${swatch(GRAPHITE, 'Provisional year, hatched and excluded', 0.4)}`;
    gapCard.append(legend);

    const note = document.createElement('p');
    note.className = 'note';
    note.innerHTML = `<strong>Click any year</strong> to set it as the comparison year for the cost bill below.
      Currently comparing <span class="mono">${esc(fy(state.year))}</span>.
      Cash receipts minus cash costs equals farm cash income exactly — that identity is ABARES' own and holds
      in all 1,146 region-years bar one rounding case, so nothing on this chart is reconstructed.`;
    gapCard.append(note);
  }

  /* ═══════════════ section 2 — what the bill is made of ═════════════════ */

  interface CostRow {
    line: Line;
    base: Cell; comp: Cell;
    baseShare: number | null; compShare: number | null;
    delta: number | null;
    ranked: boolean;
  }

  function renderComposition(): void {
    compCard.innerHTML = '';
    const region = byName.get(state.region) as Region;

    // Rule 6: each region is compared against its OWN first surveyed year. The
    // three NT regions start in 1992 and assuming 1990 would silently compare
    // them against a year they do not have.
    const baseYear = region.firstYear;
    let compYear = state.year;
    let fallbackNote = '';
    if (compYear <= baseYear) {
      const last = [...core.settledYears].reverse().find((v) => v > baseYear) ?? baseYear;
      fallbackNote = `${esc(region.name)} is first surveyed in ${esc(fy(baseYear))}, so ${esc(fy(state.year))}
        cannot be a comparison year for it. Showing ${esc(fy(last))} instead.`;
      compYear = last;
    }

    const totalBase = cell(V_COSTS, region.name, baseYear);
    const totalComp = cell(V_COSTS, region.name, compYear);

    const head = document.createElement('div');
    head.innerHTML = `
      <h2 class="card-title">What the bill is made of${glossIcon('perFarm')}</h2>
      <p class="card-sub">Each cash cost line as a share of that year's total cash cost bill, for one region.
        The bar is ${esc(fy(compYear))}; the upright tick behind it is ${esc(fy(baseYear))}, the region's first
        surveyed year. A bar that reaches past its tick is a line that has grown as a share of what the farm
        spends — which is a different question from whether it grew in dollars, and the one that says how the
        shape of farming has changed.</p>`;
    compCard.append(head);

    const controls = document.createElement('div');
    controls.className = 'controls';

    const regWrap = document.createElement('div');
    regWrap.className = 'control';
    // A <select> sizes itself to its longest option, and the longest region
    // name here is 46 characters — wide enough to push the whole page sideways
    // on a 375px screen if it is left to its intrinsic width.
    regWrap.style.flex = '1 1 260px';
    regWrap.innerHTML = '<label class="field-label" for="cg-region">Region</label>';
    const select = document.createElement('select');
    select.id = 'cg-region';
    select.dataset.role = 'region';
    select.dataset.focusKey = 'region';
    select.style.width = '100%';
    for (const r of [...core.regions].sort((a, b) => a.name.localeCompare(b.name))) {
      const o = document.createElement('option');
      o.value = r.name;
      o.textContent = r.name;
      select.append(o);
    }
    select.value = state.region;
    select.addEventListener('change', () => selectRegion(select.value));
    regWrap.append(select);
    controls.append(regWrap);

    const sortWrap = document.createElement('div');
    sortWrap.className = 'control';
    sortWrap.innerHTML = '<span class="field-label">Order</span>';
    const sortSeg = document.createElement('div');
    sortSeg.className = 'seg';
    sortSeg.setAttribute('role', 'group');
    sortSeg.setAttribute('aria-label', 'How to order the cost lines');
    for (const [key, label] of [
      ['size', `Biggest in ${fy(compYear)}`],
      ['change', 'Biggest change in share'],
    ] as Array<[Sort, string]>) {
      const b = document.createElement('button');
      b.type = 'button';
      b.textContent = label;
      b.dataset.focusKey = `sort:${key}`;
      b.setAttribute('aria-pressed', String(state.sort === key));
      b.addEventListener('click', () => {
        if (state.sort === key) return;
        state.sort = key;
        setParams({ sort: key });
        renderComposition();
        restoreFocus(compCard, `sort:${key}`);
      });
      sortSeg.append(b);
    }
    sortWrap.append(sortSeg);
    controls.append(sortWrap);
    compCard.append(controls);

    if (fallbackNote) {
      const p = document.createElement('p');
      p.className = 'note warn';
      p.innerHTML = fallbackNote;
      compCard.append(p);
    }

    // ── build the rows ────────────────────────────────────────────────────
    const rows: CostRow[] = core.costLines.map((line) => {
      const base = cell(line.variable, region.name, baseYear);
      const comp = cell(line.variable, region.name, compYear);
      const baseShare = shareOf(base.v, totalBase.v);
      const compShare = shareOf(comp.v, totalComp.v);
      // A share only enters a ranking when BOTH the line and the bill it is a
      // share of are firm or fair. A percentage-point change needs all four.
      const rankableComp = isRankable(comp.tier) && isRankable(totalComp.tier) && compShare != null;
      const rankableBase = isRankable(base.tier) && isRankable(totalBase.tier) && baseShare != null;
      const delta = rankableComp && rankableBase ? (compShare as number) - (baseShare as number) : null;
      return {
        line, base, comp, baseShare, compShare, delta,
        ranked: state.sort === 'size' ? rankableComp : delta != null,
      };
    });

    const ranked = rows.filter((r) => r.ranked).sort((a, b) => state.sort === 'size'
      ? (b.compShare ?? 0) - (a.compShare ?? 0)
      : (b.delta ?? 0) - (a.delta ?? 0));
    const unranked = rows.filter((r) => !r.ranked)
      .sort((a, b) => (b.comp.v ?? -1) - (a.comp.v ?? -1));

    // The residual is a SUBTRACTION, not a published figure, and it is never
    // ranked against the published lines. It is shown because without it the
    // bars would silently add to 86% of the bill and read as if they were all
    // of it.
    const itemisedComp = rows.reduce((s, r) => s + (r.comp.v ?? 0), 0);
    const residual = totalComp.v == null ? null : totalComp.v - itemisedComp;
    const residualShare = shareOf(residual, totalComp.v);
    const itemisedBase = rows.reduce((s, r) => s + (r.base.v ?? 0), 0);
    const residualBaseShare = shareOf(totalBase.v == null ? null : totalBase.v - itemisedBase, totalBase.v);

    const maxShare = Math.max(
      2,
      ...rows.map((r) => Math.max(r.baseShare ?? 0, r.compShare ?? 0)),
      residualShare ?? 0, residualBaseShare ?? 0,
    );

    const order: Array<CostRow | 'divider' | 'residual'> = [...ranked];
    if (unranked.length) order.push('divider', ...unranked);
    order.push('residual');

    // ── the chart ─────────────────────────────────────────────────────────
    const W = 660; const ROW = 24;
    const m = { t: 44, r: 8, b: 40, l: 178 };
    const trackR = 512;
    const H = m.t + order.length * ROW + m.b;
    const { frame, scroll } = chartFrame();
    const svg = chartSvg(W, H, `Cash cost lines as a share of the total cash cost bill, ${region.name}, ${fy(baseYear)} against ${fy(compYear)}`);
    svg.setAttribute('width', String(W));
    svg.setAttribute('height', String(H));
    svg.style.minWidth = `${W}px`;
    svg.append(hatchDefs('cg-nobasis', GRAPHITE, 0.85));

    const xs = linearScale([0, maxShare], [m.l, trackR]);
    const ticks = xs.ticks(4);
    for (const t of ticks) {
      const tx = finite(xs(t));
      svg.append(el('line', { class: t === 0 ? 'zero-line' : 'grid-line', x1: tx, y1: m.t - 8, x2: tx, y2: H - m.b + 4 }));
      const lab = el('text', { class: 'axis-text', x: tx, y: m.t - 14, 'text-anchor': 'middle' });
      lab.textContent = `${t}%`;
      svg.append(lab);
    }
    axisBottom(svg, xs, H - m.b + 4, {
      ticks, format: (v) => `${v}%`, x0: m.l, x1: trackR,
      title: 'Share of the total cash cost bill',
    });

    const colHead = el('text', { class: 'axis-title', x: W - m.r, y: m.t - 14, 'text-anchor': 'end' });
    colHead.textContent = fy(compYear);
    svg.append(colHead);

    order.forEach((row, i) => {
      const yTop = m.t + i * ROW;
      const cy = yTop + ROW / 2;

      if (row === 'divider') {
        svg.append(el('line', { x1: 8, y1: cy, x2: W - m.r, y2: cy, stroke: NEUTRAL, 'stroke-width': 1, 'stroke-dasharray': '4 3' }));
        const t = el('text', { class: 'axis-text', x: 8, y: cy - 6 });
        t.textContent = `not ranked — evidence too weak (${unranked.length})`;
        svg.append(t);
        return;
      }

      if (row === 'residual') {
        const g = el('g');
        const label = el('text', { class: 'axis-text', x: m.l - 10, y: cy + 4, 'text-anchor': 'end', fill: NEUTRAL });
        label.textContent = 'everything else';
        g.append(label);
        if (residual != null && residual >= 0 && residualShare != null) {
          const w = Math.max(1, finite(xs(residualShare)) - m.l);
          const bar = el('rect', { x: m.l, y: cy - 6, width: w, height: 12, fill: NEUTRAL, opacity: 0.55, class: 'mark' });
          markTip(bar, [
            `Everything else — ${money(residual, { long: true })}, ${pct(residualShare)} of the bill in ${fy(compYear)}`,
            'Not a published figure: it is the published total cash costs minus the 30 itemised lines.',
            'It covers cost items ABARES does not itemise in this extract, plus any line it did not publish for this region and year.',
          ]);
          g.append(bar);
          if (residualBaseShare != null) g.append(tickMark(xs, residualBaseShare, cy, 2, NEUTRAL));
          const read = el('text', { class: 'axis-text', x: W - m.r, y: cy + 4, 'text-anchor': 'end', fill: NEUTRAL });
          read.textContent = pct(residualShare);
          g.append(read);
        } else {
          const t = el('text', { class: 'axis-text', x: m.l, y: cy + 4, fill: NEUTRAL });
          t.textContent = residual == null
            ? 'no published total to subtract from'
            : 'the itemised lines add to slightly more than the published total (rounding)';
          g.append(t);
        }
        svg.append(g);
        return;
      }

      const g = el('g', { class: 'mark' });
      g.setAttribute('tabindex', '0');
      g.setAttribute('role', 'button');
      g.style.cursor = 'pointer';

      const label = el('text', { class: 'axis-text', x: m.l - 10, y: cy + 4, 'text-anchor': 'end' });
      label.textContent = clip(row.line.label, 26);
      g.append(label);

      const t = row.comp.tier;
      const colour = collapse(GRAPHITE, t, NEUTRAL);
      if (isGap(t)) {
        // Tier 5 — ABARES published nothing. The bar does not shrink to zero,
        // it is absent, and a broken stub says so.
        g.append(el('line', {
          x1: m.l, y1: cy, x2: m.l + 22, y2: cy,
          stroke: NEUTRAL, 'stroke-width': 2, 'stroke-dasharray': '3 3',
        }));
        const nt = el('text', { class: 'axis-text', x: m.l + 28, y: cy + 4, fill: NEUTRAL });
        nt.textContent = 'not published';
        g.append(nt);
      } else if (isTokenSized(t)) {
        // Tier 4 — no basis. Magnitude is NEVER encoded, so the mark is a fixed
        // token at the origin whatever the number happens to be.
        g.append(el('rect', {
          x: m.l, y: cy - 6, width: 14, height: 12,
          fill: 'url(#cg-nobasis)', stroke: NEUTRAL, 'stroke-width': 1,
        }));
        const nt = el('text', { class: 'axis-text', x: m.l + 20, y: cy + 4, fill: NEUTRAL });
        nt.textContent = 'no basis for a figure';
        g.append(nt);
      } else if (row.compShare != null) {
        const w = Math.max(1, finite(xs(row.compShare)) - m.l);
        const bar = el('rect', {
          x: m.l, y: cy - 6, width: w, height: 12,
          // Tier 3 — weak. Outline only: the mark shows where the line sits,
          // not how big it is, and the readout is tilde-prefixed.
          fill: isOutlineOnly(t) ? 'none' : colour,
          stroke: isOutlineOnly(t) ? colour : 'none',
          'stroke-width': isOutlineOnly(t) ? 1.4 : 0,
          'stroke-dasharray': isOutlineOnly(t) ? '3 2' : null,
          opacity: tierOpacity(t),
        });
        g.append(bar);
      }

      // The base-year tick. Tier 4 and tier 5 get NO tick at all rather than a
      // tick at a position their evidence cannot support.
      if (row.baseShare != null && !isTokenSized(row.base.tier) && !isGap(row.base.tier)) {
        g.append(tickMark(xs, row.baseShare, cy, isOutlineOnly(row.base.tier) ? 1.4 : 2.2, collapse(GRAPHITE, row.base.tier, NEUTRAL), isOutlineOnly(row.base.tier)));
      }

      if (row.compShare != null && !isTokenSized(t) && !isGap(t)) {
        const read = el('text', { class: 'axis-text', x: W - m.r, y: cy + 4, 'text-anchor': 'end' });
        read.textContent = `${tierPrefix(t)}${pct(row.compShare)}`;
        g.append(read);
      }

      const growth = row.base.v != null && row.base.v > 0 && row.comp.v != null
        && isRankable(row.base.tier) && isRankable(row.comp.tier)
        ? row.comp.v / row.base.v : null;

      markTip(g, [
        `${row.line.label} — ${fy(compYear)}: ${tierPrefix(t)}${money(row.comp.v, { long: true })}${row.compShare == null ? '' : `, ${tierPrefix(t)}${pct(row.compShare)} of the cash cost bill`}`,
        ...evidenceLines(row.comp.sample, t, row.comp.rse),
        `${row.line.group} · ABARES variable "${row.line.variable}"`,
        `${fy(baseYear)}: ${tierPrefix(row.base.tier)}${money(row.base.v, { long: true })}${row.baseShare == null ? '' : `, ${tierPrefix(row.base.tier)}${pct(row.baseShare)} of the bill`} (${TIER_WORD[row.base.tier]})`,
        row.delta != null && `Share of the bill has moved ${row.delta > 0 ? '+' : ''}${row.delta.toFixed(1)} percentage points`,
        // A multiple off a tiny base is arithmetic, not a trend, so it lives in
        // the tooltip beside both dollar figures and never orders the chart.
        growth != null && `In dollars that is ${growth.toFixed(2)}× — read it next to the two figures above, not on its own`,
        'Click for every year of this line',
      ]);

      const open = () => openLineDetail(row.line, region, compYear);
      g.addEventListener('click', open);
      g.addEventListener('keydown', (e) => {
        const ev = e as KeyboardEvent;
        if (ev.key === 'Enter' || ev.key === ' ') { ev.preventDefault(); open(); }
      });
      svg.append(g);
    });

    scroll.append(svg);
    compCard.append(frame);

    const legend = document.createElement('div');
    legend.className = 'legend';
    legend.innerHTML = `
      ${swatch(GRAPHITE, `Share of the bill in ${esc(fy(compYear))} — money leaving the farm`)}
      <span class="legend-item"><span class="legend-swatch" style="background:${GRAPHITE};width:3px;height:14px;border-radius:0"></span>${esc(fy(baseYear))}, the region's first surveyed year</span>
      ${swatch(NEUTRAL, 'Not itemised, or no reliable estimate', 0.55)}`;
    compCard.append(legend);

    const excluded = document.createElement('p');
    excluded.className = 'small muted';
    const rankRule = state.sort === 'size'
      ? 'A line is ranked only where its own estimate and the total it is a share of are both firm or fair.'
      : 'A change in share is ranked only where all four figures behind it — the line and the bill, in both years — are firm or fair.';
    excluded.innerHTML = `${rankRule}
      <strong>${unranked.length} of ${core.costLines.length}</strong> cost lines fall outside that rule for
      ${esc(region.name)} and are drawn below the dashed rule, unranked.
      The bill itself is <strong>${esc(TIER_WORD[totalComp.tier])}</strong> in ${esc(fy(compYear))}
      (${esc(num(totalComp.sample))} farms surveyed) and <strong>${esc(TIER_WORD[totalBase.tier])}</strong>
      in ${esc(fy(baseYear))} (${esc(num(totalBase.sample))} farms).`;
    compCard.append(excluded);

    const interestNote = document.createElement('p');
    interestNote.className = 'note';
    interestNote.innerHTML = `Interest and land rent are cash costs and appear in this bill.
      It is tempting to add them back to ${glossTerm('farmBusinessProfit', 'farm business profit')} to recover
      ${glossTerm('profitAtFullEquity', 'profit at full equity')} — that sum looks exact and is not:
      it fails in 38.4% of region-years, so this site never draws it.`;
    compCard.append(interestNote);
  }

  /** The drill-down: one cost line, every year, with the gaps left as gaps. */
  function openLineDetail(line: Line, region: Region, compYear: number): void {
    openOverlay({
      panelClass: 'drawer',
      label: `${line.label} in ${region.name}, every year`,
      lockScroll: true,
      build(panel, close) {
        const head = document.createElement('div');
        head.className = 'overlay-head';
        const h = document.createElement('h2');
        h.textContent = `${line.label} — ${region.name}`;
        head.append(h, closeButton(close, 'Close this cost line'));
        panel.append(head);

        const sub = document.createElement('p');
        sub.className = 'small muted';
        sub.innerHTML = `${esc(line.variable)} · ${esc(line.group)} · per surveyed farm, ${glossTerm('nominal', 'nominal dollars')}.
          The line BREAKS in any year ABARES published no estimate — nothing is bridged or filled in.`;
        panel.append(sub);

        const pts = core.years.map((yr) => ({ year: yr, c: cell(line.variable, region.name, yr) }));
        const vals = pts.map((p) => p.c.v);
        const [lo, hi] = extent(vals);
        const W = 520; const H = 180; const m = { t: 14, r: 14, b: 34, l: 62 };
        const { frame, scroll } = chartFrame();
        const svg = chartSvg(W, H, `${line.label} per surveyed farm in ${region.name}, ${fy(core.years[0])} to ${fy(core.years[core.years.length - 1])}`);
        svg.setAttribute('width', String(W));
        svg.setAttribute('height', String(H));
        svg.style.minWidth = `${W}px`;
        const x = linearScale([core.years[0], core.years[core.years.length - 1]], [m.l, W - m.r]);
        const y = linearScale([Math.min(0, lo), Math.max(hi, 1)], [H - m.b, m.t]);
        axisLeft(svg, y, m.l, { format: (v) => money(v), gridTo: W - m.r });
        axisBottom(svg, x, H - m.b, { format: (v) => String(v), ticks: core.years.filter((v) => v % 10 === 0) });

        // Settled years only in the solid run; the provisional year is drawn as
        // its own dashed tail so no eye reads it as part of the trend.
        const settled = pts.filter((p) => p.year !== core.provisionalYear);
        const linePts = settled.map((p) => ({ x: finite(x(p.year)), y: p.c.v == null ? null : finite(y(p.c.v)) }));
        for (const d of brokenPath(linePts)) {
          svg.append(el('path', { d, fill: 'none', stroke: GRAPHITE, 'stroke-width': 2, opacity: 0.9 }));
        }
        for (const p of isolatedPoints(linePts)) {
          svg.append(el('circle', { cx: p.x, cy: p.y, r: 2.6, fill: GRAPHITE }));
        }
        const prov = pts.find((p) => p.year === core.provisionalYear);
        const lastSettled = [...settled].reverse().find((p) => p.c.v != null);
        if (prov?.c.v != null && lastSettled?.c.v != null) {
          svg.append(el('path', {
            d: `M${finite(x(lastSettled.year)).toFixed(2)},${finite(y(lastSettled.c.v)).toFixed(2)} L${finite(x(prov.year)).toFixed(2)},${finite(y(prov.c.v)).toFixed(2)}`,
            fill: 'none', stroke: GRAPHITE, 'stroke-width': 2, 'stroke-dasharray': '4 3', opacity: 0.6,
          }));
        }
        for (const p of pts) {
          if (p.c.v == null) continue;
          const t = p.c.tier;
          const dot = el('circle', {
            cx: finite(x(p.year)),
            // Tier 4 dots are drawn at a fixed radius like every other dot —
            // radius encodes nothing here, position does — but they are hatched
            // hollow so they cannot be read as a firm value.
            cy: finite(y(p.c.v)), r: 3.4,
            fill: isOutlineOnly(t) || isTokenSized(t) ? 'none' : collapse(GRAPHITE, t, NEUTRAL),
            stroke: collapse(GRAPHITE, t, NEUTRAL), 'stroke-width': 1.2,
            opacity: tierOpacity(t), class: 'mark',
          });
          markTip(dot, [
            `${fy(p.year)}: ${tierPrefix(t)}${money(p.c.v, { long: true })}${p.year === core.provisionalYear ? ' (provisional)' : ''}`,
            ...evidenceLines(p.c.sample, t, p.c.rse),
          ]);
          svg.append(dot);
        }
        scroll.append(svg);
        panel.append(frame);

        const wrap = document.createElement('div');
        wrap.className = 'table-scroll';
        const rowsHtml = pts.map((p) => {
          const share = shareOf(p.c.v, cell(V_COSTS, region.name, p.year).v);
          return `<tr${p.year === compYear ? ' style="background:var(--accent-soft)"' : ''}>
            <td class="mono">${esc(fy(p.year))}${p.year === core.provisionalYear ? ' <span class="pill">prov.</span>' : ''}</td>
            <td class="num">${esc(tierPrefix(p.c.tier))}${esc(money(p.c.v, { long: true }))}</td>
            <td class="num">${share == null ? '—' : esc(`${tierPrefix(p.c.tier)}${pct(share)}`)}</td>
            <td class="num">${esc(num(p.c.sample))}</td>
            <td data-tip="${tipAttr(TIER_MEANING[p.c.tier])}">${esc(TIER_WORD[p.c.tier])}</td>
          </tr>`;
        }).join('');
        wrap.innerHTML = `<table>
          <caption class="visually-hidden">${esc(line.label)} in ${esc(region.name)}, every surveyed year</caption>
          <thead><tr><th>Year</th><th class="num">Cost</th><th class="num">Share of bill</th>
            <th class="num">Farms</th><th>Evidence</th></tr></thead>
          <tbody>${rowsHtml}</tbody></table>`;
        panel.append(wrap);
      },
    });
  }

  /* ════════════════ section 3 — where the gap is widest ═════════════════ */

  function renderTable(): void {
    tableCard.innerHTML = '';

    const MIN_YEARS = 20;
    interface Lead {
      name: string; zone: string; share: number | null;
      used: number; skipped: number; over: number; sample: number | null;
    }
    const leads: Lead[] = core.regions.map((r) => {
      let sum = 0; let used = 0; let skipped = 0; let over = 0;
      for (const s of r.series) {
        // Rule 5: the provisional year never enters a mean.
        if (s.year === core.provisionalYear) continue;
        const c = cell(V_COSTS, r.name, s.year);
        const rc = cell(V_RECEIPTS, r.name, s.year);
        if (c.v == null || rc.v == null || rc.v <= 0 || !isRankable(c.tier) || !isRankable(rc.tier)) { skipped++; continue; }
        sum += (c.v / rc.v) * 100;
        // Counted over exactly the same years as the mean beside it, so the two
        // columns cannot disagree about which years they are describing.
        if (c.v > rc.v) over++;
        used++;
      }
      return {
        name: r.name, zone: r.zone, used, skipped, over, sample: r.medianSample,
        share: used >= MIN_YEARS ? sum / used : null,
      };
    });

    const shown = leads.filter((l) => l.share != null).sort((a, b) => (b.share as number) - (a.share as number));
    const held = leads.filter((l) => l.share == null).sort((a, b) => b.used - a.used);
    const worst = shown[0];
    const best = shown[shown.length - 1];

    const head = document.createElement('div');
    head.innerHTML = `
      <h2 class="card-title">Where the gap is widest${glossIcon('rse')}</h2>
      <p class="card-sub">Cash costs as a share of cash receipts, averaged across the settled years where both
        figures are firm or fair. Read it as cents in every dollar of receipts that went straight back out
        again. Lower is a farm business keeping more of what it sells.</p>`;
    tableCard.append(head);

    const wrap = document.createElement('div');
    wrap.className = 'table-scroll';
    const body = shown.map((l) => {
      // The bar starts at 55% rather than 0 — every region is above 60, so a
      // bar anchored at zero would be 32 near-identical full-width bars. The
      // number is printed beside it and the axis of the claim is the column,
      // not the bar, so this is a reading aid, not the encoding.
      const w = Math.max(2, Math.min(100, ((l.share as number) - 55) * (100 / 40)));
      // The whole row is clickable for a pointer, but the keyboard and screen
      // reader path is a REAL button inside the first cell. `role="button"` on
      // the <tr> itself would overwrite the row semantics of the table it is
      // in, and a bare focusable <tr> announces no action at all.
      return `<tr class="clickable" data-region="${esc(l.name)}"
          data-tip="${tipAttr(
            `${l.name} — ${pct(l.share)} of receipts went out as cash costs`,
            `averaged over ${l.used} settled years; ${l.skipped} year${l.skipped === 1 ? '' : 's'} left out for weak evidence`,
            'click to load this region into the cost bill above',
          )}">
        <td class="truncate"><button type="button" data-pick="${esc(l.name)}"
          style="background:none;border:0;padding:0;font:inherit;color:inherit;text-align:left;cursor:pointer"
          >${esc(l.name)}</button></td>
        <td class="small muted nowrap">${esc(l.zone)}</td>
        <td class="num">${esc(pct(l.share))}</td>
        <td><span style="display:block;height:9px;border-radius:2px;background:${GRAPHITE};opacity:.7;width:${w.toFixed(1)}%"></span></td>
        <td class="num"${l.over ? ` style="color:${CRIMSON};font-weight:600"` : ''}>${l.over || '—'}</td>
        <td class="num">${l.used}</td>
        <td class="num">${esc(num(l.sample))}</td>
      </tr>`;
    }).join('');
    wrap.innerHTML = `<table>
      <caption class="visually-hidden">Cash costs as a share of cash receipts, by region</caption>
      <thead><tr>
        <th>Region</th><th>Zone</th><th class="num">Costs per $1 of receipts</th>
        <th><span class="visually-hidden">bar</span></th>
        <th class="num" data-tip="${tipAttr(
          'Years in which cash costs exceeded cash receipts outright',
          'Farm cash income was below zero — the farm spent more cash than it took in',
        )}">Loss years</th>
        <th class="num" data-tip="${tipAttr('Settled years that survived the evidence rule')}">Years</th>
        <th class="num" data-tip="${tipAttr('Median number of farms surveyed in this region')}">Farms</th>
      </tr></thead>
      <tbody>${body}</tbody></table>`;
    tableCard.append(wrap);

    for (const tr of wrap.querySelectorAll<HTMLTableRowElement>('tr[data-region]')) {
      const name = tr.dataset.region as string;
      markSelected(tr, name === state.region);
      // One delegated-ish listener per row, on an element inside `host`, so it
      // dies with the host and cannot leak across a navigation.
      tr.addEventListener('click', () => selectRegion(name));
    }

    const foot = document.createElement('p');
    foot.className = 'small muted';
    foot.innerHTML = `A region enters this ranking only where at least ${MIN_YEARS} of the
      ${core.settledYears.length} settled years survive the evidence rule.
      <strong>${held.length} of ${core.regions.length}</strong> regions do not and are left out entirely:
      ${held.map((h) => `${esc(h.name)} (${h.used} year${h.used === 1 ? '' : 's'})`).join(', ')}.
      They are the smallest samples in the survey, not the least interesting places.`;
    tableCard.append(foot);

    if (worst && best) {
      const lossYears = shown.reduce((s, l) => s + l.over, 0);
      const lossRegions = shown.filter((l) => l.over > 0).length;
      const story = document.createElement('p');
      story.className = 'note';
      story.innerHTML = `The spread is about ${esc(pct((worst.share as number) - (best.share as number), 0))} of receipts wide.
        In ${esc(worst.name)} roughly ${esc(Math.round(worst.share as number).toString())}c in every dollar of receipts
        left again as cash costs; in ${esc(best.name)} it was about ${esc(Math.round(best.share as number).toString())}c.
        That is before depreciation and before anyone in the family draws a wage, so it is the most flattering
        version of the number available. And an average hides the bad years:
        <strong style="color:${CRIMSON}">${lossYears} region-years</strong> in ${lossRegions} of these
        ${shown.length} regions had cash costs exceeding cash receipts outright — a year in which the farm
        spent more cash than it took in, before any of the deductions above.`;
      tableCard.append(story);
    }
  }

  renderGap();
  renderComposition();
  renderTable();
}

/* ─────────────────────────────── helpers ──────────────────────────────────── */

/**
 * The selected row.
 *
 * Set inline rather than by class: this view owns exactly one file and cannot
 * add a rule to the stylesheet, and `tbody tr:nth-child(even)` would outrank a
 * new class anyway. `aria-selected` carries the same fact to a screen reader.
 */
function markSelected(tr: HTMLTableRowElement, on: boolean): void {
  tr.style.background = on ? 'var(--accent-soft)' : '';
  tr.style.boxShadow = on ? 'inset 3px 0 0 var(--accent-primary)' : '';
  tr.setAttribute('aria-selected', on ? 'true' : 'false');
}

/**
 * Carry keyboard focus across a rebuild.
 *
 * Every control in this view re-renders the section it lives in, which detaches
 * the very element the user is standing on. `focusKey` reads the identity of
 * whatever is focused inside `container` before the rebuild; `restoreFocus`
 * puts focus back on its replacement afterwards.
 */
function focusKey(container: HTMLElement): string | null {
  const active = document.activeElement as HTMLElement | null;
  if (!active || !container.contains(active)) return null;
  return active.dataset?.focusKey ?? null;
}

function restoreFocus(container: HTMLElement, key: string | null): void {
  if (!key) return;
  // Matched by reading the attribute rather than by interpolating it into a
  // selector: `CSS.escape` does not exist in jsdom, so a selector built with it
  // throws in the test environment this site is tested in, and one built
  // without it would break on any key containing a quote.
  for (const node of container.querySelectorAll<HTMLElement>('[data-focus-key]')) {
    if (node.dataset.focusKey === key) { node.focus(); return; }
  }
}

/** Truncate for an SVG <text> node, which takes RAW text via textContent. */
function clip(s: string, max: number): string {
  return s.length <= max ? s : `${s.slice(0, max - 1).trimEnd()}…`;
}

function shareOf(v: number | null, total: number | null): number | null {
  if (v == null || total == null || !Number.isFinite(v) || !Number.isFinite(total) || total <= 0) return null;
  return (v / total) * 100;
}

/** Consecutive items that all satisfy `ok` — never one span bridging a gap. */
function runs<T>(items: T[], ok: (t: T) => boolean): T[][] {
  const out: T[][] = [];
  let cur: T[] = [];
  for (const it of items) {
    if (ok(it)) cur.push(it);
    else { if (cur.length) out.push(cur); cur = []; }
  }
  if (cur.length) out.push(cur);
  return out;
}

function tickMark(
  xs: ReturnType<typeof linearScale>, share: number, cy: number,
  width: number, colour: string, dashed = false,
): SVGLineElement {
  const tx = finite(xs(share));
  return el('line', {
    x1: tx, y1: cy - 9, x2: tx, y2: cy + 9,
    stroke: colour, 'stroke-width': width,
    'stroke-dasharray': dashed ? '3 2' : null,
    'pointer-events': 'none',
  });
}

function drawSeries(
  svg: SVGSVGElement,
  pts: Array<{ x: number; y: number | null }>,
  colour: string, width: number, label: string,
): void {
  for (const d of brokenPath(pts)) {
    const p = el('path', { d, fill: 'none', stroke: colour, 'stroke-width': width, 'stroke-linejoin': 'round' });
    markTip(p, [label]);
    svg.append(p);
  }
  for (const p of isolatedPoints(pts)) {
    svg.append(el('circle', { cx: p.x, cy: p.y, r: 2.8, fill: colour }));
  }
}

function swatch(colour: string, label: string, opacity = 1): string {
  return `<span class="legend-item"><span class="legend-swatch" style="background:${colour};opacity:${opacity}"></span>${label}</span>`;
}

function buildHead(core: Core): HTMLElement {
  const head = document.createElement('div');
  head.className = 'view-head';
  head.innerHTML = `
    <h1>The cost gap</h1>
    <p class="lede">Most of what a ${glossTerm('broadacre')} farm sells never becomes income. It goes back out
      the same year as cash costs — fertiliser, chemicals, fuel, contractors, interest, insurance. This page
      follows the money that is left, and what the bill in front of it is made of, from
      ${esc(fy(core.years[0]))} to ${esc(fy(core.years[core.years.length - 1])) }.</p>
    <p class="small muted">Every figure is ${glossTerm('perFarm', 'per surveyed farm')} and comes from the
      ${glossTerm('aagis')}. Years are ${glossTerm('financialYear', 'financial years')}.</p>`;
  return head;
}

function buildStats(core: Core): HTMLElement {
  const wrap = document.createElement('div');
  wrap.className = 'stat-row';
  wrap.style.marginBottom = 'var(--space-lg)';

  const first = core.national.find((n) => n.year === core.years[0]);
  const lastSettled = core.national.find((n) => n.year === core.settledYears[core.settledYears.length - 1]);
  if (!first || !lastSettled) return wrap;
  const span = `${fy(first.year)} → ${fy(lastSettled.year)}`;
  const mult = (a: number | null, b: number | null) =>
    a != null && b != null && a > 0 ? `${(b / a).toFixed(2)}×` : '—';

  const cards: Array<{ v: string; cls: string; label: string; note: string; tipLines: string[] }> = [
    {
      v: mult(first.receipts, lastSettled.receipts), cls: 'farming',
      label: 'Cash receipts per surveyed farm',
      note: `${money(first.receipts)} → ${money(lastSettled.receipts)}`,
      tipLines: [`Total cash receipts, ${span}`, `${money(first.receipts, { long: true })} → ${money(lastSettled.receipts, { long: true })}`, 'National published estimate — no sampling error is published for it'],
    },
    {
      v: mult(first.costs, lastSettled.costs), cls: '',
      label: 'Cash costs per surveyed farm',
      note: `${money(first.costs)} → ${money(lastSettled.costs)}`,
      tipLines: [`Total cash costs, ${span}`, `${money(first.costs, { long: true })} → ${money(lastSettled.costs, { long: true })}`, 'The bill grew a little faster than the receipts did'],
    },
    {
      v: mult(first.cashIncome, lastSettled.cashIncome), cls: 'farming',
      label: 'What was left — farm cash income',
      note: `${money(first.cashIncome)} → ${money(lastSettled.cashIncome)}`,
      tipLines: [`Farm cash income, ${span}`, `${money(first.cashIncome, { long: true })} → ${money(lastSettled.cashIncome, { long: true })}`, 'Receipts minus costs, exactly as ABARES publishes both'],
    },
    {
      v: mult(first.landValue, lastSettled.landValue), cls: 'land',
      label: 'The land underneath, same years',
      note: `${money(first.landValue)} → ${money(lastSettled.landValue)}`,
      tipLines: [`Value of land and fixed improvements, ${span}`, `${money(first.landValue, { long: true })} → ${money(lastSettled.landValue, { long: true })}`, 'This is the comparison the rest of the site is about'],
    },
  ];

  wrap.innerHTML = cards.map((c) => `
    <div class="stat" data-tip="${tipAttr(...c.tipLines)}">
      <div class="stat-value ${c.cls}">${esc(c.v)}</div>
      <div class="stat-label">${esc(c.label)}</div>
      <div class="stat-note mono">${esc(c.note)}</div>
    </div>`).join('');
  return wrap;
}

function buildFooterNotes(core: Core): HTMLElement {
  const wrap = document.createElement('div');
  wrap.className = 'stack';
  wrap.style.marginTop = 'var(--space-lg)';

  const key = document.createElement('div');
  key.className = 'card';
  key.innerHTML = `
    <h3 class="card-title">How to read the evidence on this page</h3>
    <p class="card-sub">Every cost figure here is a survey estimate with a published error, and this page
      draws that error rather than footnoting it. ${glossTerm('rse', 'What the error means')}.</p>
    <ul class="stack small" style="margin-top:var(--space-md);padding-left:1.1rem">
      <li><strong>${esc(TIER_WORD[1])}</strong> and <strong>${esc(TIER_WORD[2])}</strong> — drawn solid.
        These are the only figures allowed into a ranking or an average anywhere on this page.</li>
      <li><strong>${esc(TIER_WORD[3])}</strong> — drawn as an outline and printed with a tilde
        (<span class="mono">~4.1%</span>). Read the direction, not the number.</li>
      <li><strong>${esc(TIER_WORD[4])}</strong> — drawn as a fixed hatched token that is the same size
        whatever the figure is. The survey cannot support a magnitude here, so none is shown.</li>
      <li><strong>${esc(TIER_WORD[5])}</strong> — nothing is drawn and the line breaks.
        No value is ever interpolated or filled with zero.</li>
      <li>The ${glossTerm('provisional', 'provisional year')} (${esc(fy(core.provisionalYear ?? 0))}) is hatched
        and takes no part in any comparison, ranking or average on this page.</li>
    </ul>`;
  wrap.append(key);

  const scope = document.createElement('p');
  scope.className = 'xsmall muted';
  scope.style.marginTop = 'var(--space-md)';
  scope.textContent = SCOPE_SHORT;
  wrap.append(scope);

  const src = document.createElement('p');
  src.className = 'xsmall muted';
  src.innerHTML = `Cost lines are the 30 itemised cash cost variables ABARES publishes per region;
    they do not add to total cash costs, and the shortfall is shown on the chart rather than hidden by
    rescaling. Data as at ${esc(core.asAt)}.`;
  wrap.append(src);

  // CRIMSON is imported and reserved: nothing on this page falls below zero
  // nationally, and the guard in renderGap() is what keeps it that way. Naming
  // it here keeps the palette contract visible to the next reader.
  const belowZero = document.createElement('p');
  belowZero.className = 'xsmall muted';
  belowZero.innerHTML = `<span style="color:${CRIMSON}">Below zero</span> is the only thing this site ever
    colours red. Nationally, cash costs have not exceeded cash receipts in any year drawn on this page,
    so no mark here is red. Regionally they have, which is what the ranking above is measuring.`;
  wrap.append(belowZero);

  return wrap;
}
